import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
// import { zoomAuthCallback } from "../../api/zoom";
import { Loader2 } from "lucide-react";
import {
  registerCalendlyWebhookSubscription,
  getCalendlyMeInfo,
  getOrganizationRegisteredWebhookUrls,
  postCalendlyTherawinMap,
  getUserRegisteredWebhookUrls,
} from "../../api/calendly";
import config from "../../utils/config";

function CalendlyCallback() {
  // "loading" | "success" | "error"
  const [status, setStatus] = useState("loading");
  const [message, setMessage] = useState(
    "We are connecting your Calendly account. Please wait..."
  );

  const therapist_id = useSelector((store) => store.therapist.therapist_id);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const access_token = params.get("access_token");
    const refresh_token = params.get("refresh_token");
    const expires_in = params.get("expires_in");

    if (!access_token) {
      setStatus("error");
      setMessage(
        "No authorization code found. Please re-initiate Calendly connection."
      );
      return;
    }

    const calendly_webhook_registration = async () => {
      try {
        const scope = "user";
        const lambda_endpoint = config.API_URL + "/calendly/calendly_webhook";

        const meInfo = await getCalendlyMeInfo(access_token);
        console.log(
          "DEBUG LOG: Calendly Me Information response:",
          meInfo.data
        );
        const current_organization = meInfo.data.resource.current_organization;
        const current_user = meInfo.data.resource.uri;

        let registeredWebhookUrls = {};
        try {
          registeredWebhookUrls = await getOrganizationRegisteredWebhookUrls(
            access_token,
            current_organization
          );
          const exists = registeredWebhookUrls.collection.some(
            (entry) => entry.callback_url === lambda_endpoint
          );
          if (exists) {
            console.log("Webhook already registered for organization scope.");
            await postCalendlyTherawinMap(
              therapist_id,
              meInfo.data.resource.uri,
              meInfo.data.resource.email,
              access_token,
              refresh_token,
              expires_in,
              true
            );
            // Store success status for redirect
            sessionStorage.setItem(
              "calendly_webhook_status",
              JSON.stringify({
                status: "success",
                message: "Calendly webhook already registered.",
              })
            );
            return;
          }
        } catch (error) {
          console.log(
            "Error fetching organization registered webhook URLs:",
            error
          );
        }

        registeredWebhookUrls = await getUserRegisteredWebhookUrls(
          access_token,
          current_organization,
          current_user
        );
        const exists = registeredWebhookUrls.collection.some(
          (entry) => entry.callback_url === lambda_endpoint
        );
        if (exists) {
          console.log("Webhook already registered under user scope.");
          await postCalendlyTherawinMap(
            therapist_id,
            meInfo.data.resource.uri,
            meInfo.data.resource.email,
            access_token,
            refresh_token,
            expires_in,
            true
          );
          // Store success status for redirect
          sessionStorage.setItem(
            "calendly_webhook_status",
            JSON.stringify({
              status: "success",
              message: "Calendly webhook already registered.",
            })
          );
          return;
        }

        console.log("Registered Webhook URLs response:", registeredWebhookUrls);

        try {
          const res = await registerCalendlyWebhookSubscription(
            access_token,
            lambda_endpoint,
            current_organization,
            current_user,
            scope
          );
          console.log("Webhook registration response:", res);
          await postCalendlyTherawinMap(
            therapist_id,
            meInfo.data.resource.uri,
            meInfo.data.resource.email,
            access_token,
            refresh_token,
            expires_in,
            true
          );
          // Store success status for redirect
          sessionStorage.setItem(
            "calendly_webhook_status",
            JSON.stringify({
              status: "success",
              message: "Calendly webhook registered successfully.",
            })
          );
        } catch (err) {
          // Store error status for redirect
          sessionStorage.setItem(
            "calendly_webhook_status",
            JSON.stringify({
              status: "error",
              message:
                err?.response?.data?.message ||
                err?.message ||
                "Calendly webhook registration failed.",
            })
          );
          throw err;
        }
        setStatus("success");
        setMessage("Your Calendly account is now connected! Redirecting...");
      } catch (error) {
        console.error("Error registering Calendly user info:", error);
        setStatus("error");
        setMessage("Could not register Calendly webhook. Please try again.");
        sessionStorage.setItem(
          "calendly_webhook_status",
          JSON.stringify({
            status: "error",
            message:
              error?.response?.data?.message ||
              error?.message ||
              "Could not register Calendly webhook. Please try again.",
          })
        );
        return;
      } finally {
        setTimeout(() => {
          window.location.href = "/zoom/connect";
        }, 2000);
      }
    };
    calendly_webhook_registration();
  }, [therapist_id]);

  // Decide title based on status
  const getTitle = () => {
    switch (status) {
      case "loading":
        return "Calendly Account Connection";
      case "success":
        return "Calendly Connection Successful";
      case "error":
        return "Calendly Connection Failed";
      default:
        return "Calendly Account Connection";
    }
  };

  return (
    <div className="my-10 mx-8 text-center flex flex-col items-center">
      <h1 className="text-3xl font-bold mb-6">{getTitle()}</h1>

      {status === "loading" ? (
        <div className="flex items-center gap-4">
          <Loader2 className="h-10 w-10 animate-spin" />
          <p className="text-xl">{message}</p>
        </div>
      ) : (
        <p className="text-xl">{message}</p>
      )}
    </div>
  );
}

export default CalendlyCallback;
